/**
 * The affine collapse (plan hypothesis T2).
 *
 * Every stage of the decorrelation stretch except the final clamp is linear,
 * so the seven steps of `core/stretch.ts` collapse into
 *
 *     out = clamp(M x + b, 0, 255)
 *
 * with
 *
 *     M = R D E^T
 *     b = t + R c + o - M mu
 *
 * where
 *   - `E` is the eigenvector matrix (columns = eigenvectors, descending lambda),
 *   - `D = diag(gain / sqrt(max(lambda_i, floor)))`, capped at `dMax`,
 *   - `R` is `recolor`,
 *   - `c` is `bandOffset` (applied in stretched space, so it passes through `R`),
 *   - `o` is `outputOffset` (applied in output space, so it does not),
 *   - `t` is the recentring translation for `recolorMode`,
 *   - `mu` is the image mean.
 *
 * The `M x + b` form costs 9 multiply-adds per pixel instead of 27 for the
 * naive staged form, and it removes the per-pixel branch work entirely. On the
 * GPU it is one `mat3` multiply; on an integer CPU it is nine table lookups
 * (see `core/luts.ts`, hypothesis T3).
 *
 * @module core/compose
 */

import type { Affine, StretchParams, Vec3 } from './types.ts';
import { mul, mulVec, transpose } from './mat3.ts';
import {
  applyStagedPreClamp,
  clamp255,
  factorMatrix,
  recolorTranslation,
  stagedSteps,
  stretchFactors,
} from './stretch.ts';
import { jacobiEigen } from './jacobi.ts';

/**
 * Collapse `StretchParams` into `{M, b}`.
 *
 * The clamp is deliberately *not* applied here; it belongs to `apply.ts`, so
 * that the property test can compare pre-clamp values exactly.
 */
export function collapse(params: StretchParams): Affine {
  const eigen = jacobiEigen(params.covariance);
  const rotation = transpose(eigen.vectors); // E^T
  const factors = stretchFactors(
    eigen.values,
    params.gain ?? 1,
    params.lambdaFloor,
    params.dMax,
  );

  const recolor = params.recolor ?? null;
  const stage = recolor ? mul(recolor, factorMatrix(factors)) : factorMatrix(factors);
  // stage = R D  (or just D when R is the identity)
  const M = mul(stage, rotation); // R D E^T

  const translation = recolorTranslation(params.recolorMode, params.mean);
  const bandOffset = params.bandOffset ?? null;
  const outputOffset = params.outputOffset ?? null;

  const Mmu = mulVec(M, params.mean);
  const b = new Float64Array(3);

  // b = t - M mu
  b[0] = translation[0]! - Mmu[0]!;
  b[1] = translation[1]! - Mmu[1]!;
  b[2] = translation[2]! - Mmu[2]!;

  // ... + R c, where c is the stretched-space bias.
  if (bandOffset) {
    const Rc = recolor ? mulVec(recolor, bandOffset) : Float64Array.from(bandOffset);
    b[0] = b[0]! + Rc[0]!;
    b[1] = b[1]! + Rc[1]!;
    b[2] = b[2]! + Rc[2]!;
  }

  // ... + o, the output-space bias.
  if (outputOffset) {
    b[0] = b[0]! + outputOffset[0]!;
    b[1] = b[1]! + outputOffset[1]!;
    b[2] = b[2]! + outputOffset[2]!;
  }

  return { M, b };
}

/**
 * Exact per-pixel reference: the full staged pipeline. Slow, used by tests and
 * by the differential check in `apply.test.ts`.
 */
export function applyStagedReference(params: StretchParams, x: ArrayLike<number>): Vec3 {
  return applyStagedPreClamp(stagedSteps(params), x);
}

/**
 * Maximum absolute difference between the staged and collapsed pre-clamp
 * outputs over a list of pixels. This is the number the property test asserts
 * is below `1e-6`.
 */
export function maxCollapseError(params: StretchParams, pixels: ArrayLike<ArrayLike<number>>): number {
  const affine = collapse(params);
  const steps = stagedSteps(params);
  let worst = 0;
  for (let i = 0; i < pixels.length; i++) {
    const x = pixels[i]!;
    const staged = applyStagedPreClamp(steps, x);
    const collapsed = Float64Array.of(
      affine.M[0]! * x[0]! + affine.M[3]! * x[1]! + affine.M[6]! * x[2]! + affine.b[0]!,
      affine.M[1]! * x[0]! + affine.M[4]! * x[1]! + affine.M[7]! * x[2]! + affine.b[1]!,
      affine.M[2]! * x[0]! + affine.M[5]! * x[1]! + affine.M[8]! * x[2]! + affine.b[2]!,
    );
    for (let c = 0; c < 3; c++) worst = Math.max(worst, Math.abs(staged[c]! - collapsed[c]!));
  }
  return worst;
}

/**
 * Fold two affines: `outer(inner(x))`.
 *
 * Kept for composing a user adjustment on top of a preset without rebuilding
 * the pipeline.
 */
export function composeAffine(outer: Affine, inner: Affine): Affine {
  const b = mulVec(outer.M, inner.b);
  b[0] = b[0]! + outer.b[0]!;
  b[1] = b[1]! + outer.b[1]!;
  b[2] = b[2]! + outer.b[2]!;
  return { M: mul(outer.M, inner.M), b };
}

/** Apply an affine to one pixel, with clamping. */
export function applyAffine(affine: Affine, x: ArrayLike<number>, clamp = true): Vec3 {
  const out = Float64Array.of(
    affine.M[0]! * x[0]! + affine.M[3]! * x[1]! + affine.M[6]! * x[2]! + affine.b[0]!,
    affine.M[1]! * x[0]! + affine.M[4]! * x[1]! + affine.M[7]! * x[2]! + affine.b[1]!,
    affine.M[2]! * x[0]! + affine.M[5]! * x[1]! + affine.M[8]! * x[2]! + affine.b[2]!,
  );
  if (clamp) {
    out[0] = clamp255(out[0]!);
    out[1] = clamp255(out[1]!);
    out[2] = clamp255(out[2]!);
  }
  return out;
}

/** The identity affine. */
export function identityAffine(): Affine {
  const M = new Float64Array(9);
  M[0] = 1;
  M[4] = 1;
  M[8] = 1;
  return { M, b: new Float64Array(3) };
}
